/**
 * Centralized routing configuration for VaksineTorget
 * 
 * All routes are defined in Norwegian for better SEO and user experience.
 * Use these constants throughout the application instead of hardcoded strings.
 */

// Public routes
export const ROUTES = {
  HOME: '/',
  
  // Authentication
  LOGIN: '/logg-inn',
  REGISTER: '/registrer',
  REGISTER_WIZARD: '/registrer-veiviser',
  
  // Onboarding
  ONBOARDING_COMPANY: '/bli-bedrift',
  ONBOARDING_PROVIDER: '/bli-tilbyder',
  
  // Dashboards
  COMPANY_DASHBOARD: '/bedrift',
  PROVIDER_DASHBOARD: '/tilbyder',
  ADMIN_DASHBOARD: '/admin',
  ADMIN_IMPERSONATION: '/admin/vis-som',
  
  // Search and booking
  SEARCH: '/finn-tilbyder',
  BOOKING: '/bestilling',
  
  // Detail pages
  PROVIDER_DETAIL: '/tilbyder',
  COMPANY_DETAIL: '/bedrift',
  IMPORTED_PROVIDER_DETAIL: '/admin/importerte-tilbydere',
  ADMIN_PROVIDER_DETAIL: '/admin/tilbydere',
  ADMIN_COMPANY_DETAIL: '/admin/bedrifter',
  COMPANY_PROVIDER_DETAIL: '/bedrift/tilbydere',
  
  // Informational pages
  HOW_IT_WORKS: '/hvordan-det-fungerer',
  FOR_COMPANIES: '/for-bedrifter',
  FOR_PROVIDERS: '/for-tilbydere',
  ABOUT: '/om-oss',
  CONTACT: '/kontakt',
  SAVINGS_CALCULATOR: '/hvor-mye-kan-jeg-spare',
  PRIVACY_TERMS: '/personvern-og-vilkar',
  SECURITY: '/sikkerhet',
  
  // Error pages
  NOT_FOUND: '/404',
} as const;

// Helper functions for generating dynamic routes
export const getDashboardRoute = (role: "company" | "provider" | "admin"): string => {
  switch (role) {
    case "company":
      return ROUTES.COMPANY_DASHBOARD;
    case "provider":
      return ROUTES.PROVIDER_DASHBOARD;
    case "admin":
      return ROUTES.ADMIN_DASHBOARD;
    default:
      return ROUTES.HOME;
  }
};

/**
 * Generate provider detail route - uses slug if available for SEO-friendly URLs
 */
export const getProviderDetailRoute = (providerIdOrSlug: string): string => {
  return `/tilbyder/${providerIdOrSlug}`;
};

/**
 * Generate admin provider detail route - uses slug if available
 */
export const getAdminProviderDetailRoute = (providerIdOrSlug: string): string => {
  return `/admin/tilbydere/${providerIdOrSlug}`;
};

/**
 * Generate imported provider detail route - uses ID (imported providers don't have slugs yet)
 */
export const getImportedProviderDetailRoute = (providerId: string): string => {
  return `/admin/importerte-tilbydere/${providerId}`;
};

/**
 * Generate company provider detail route - uses slug if available
 */
export const getCompanyProviderDetailRoute = (providerIdOrSlug: string): string => {
  return `/bedrift/tilbydere/${providerIdOrSlug}`;
};

/**
 * Generate admin company detail route - uses slug if available
 */
export const getAdminCompanyDetailRoute = (companyIdOrSlug: string): string => {
  return `/admin/bedrifter/${companyIdOrSlug}`;
};

/**
 * Generate booking detail route
 */
export const getBookingDetailRoute = (bookingId: string): string => {
  return `/bestilling/${bookingId}`;
};

// Legacy helper for backward compatibility
export const getBookingRoute = getBookingDetailRoute;

// Legacy routes for backward compatibility (used in redirects)
export const LEGACY_ROUTES = {
  AUTH: '/auth',
  ONBOARDING_COMPANY: '/onboarding/company',
  ONBOARDING_PROVIDER: '/onboarding/provider',
  COMPANY_DASHBOARD: '/dashboard/company',
  PROVIDER_DASHBOARD: '/dashboard/provider',
  ADMIN_DASHBOARD: '/dashboard/admin',
  ADMIN_PROVIDER_DETAIL: '/dashboard/admin/providers',
  ADMIN_COMPANY_DETAIL: '/dashboard/admin/companies',
  COMPANY_PROVIDER_DETAIL: '/dashboard/company/providers',
  IMPORTED_PROVIDER_DETAIL: '/dashboard/admin/imported-providers',
  SEARCH: '/search',
  BOOKING: '/booking',
} as const;
